import styles from './styles.module.scss';
import { DailySunlightResult } from '@/types/sunlight';
import { calculateSunlightScore } from '@/services/score/sunlight';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';

interface DetailedGridPointDataProps {
	data: DailySunlightResult[];
}

const formatDate = (dateStr: string | null | undefined) => {
	if (!dateStr) return 'N/A';
	const date = new Date(dateStr);
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
	});
};

const average = (values: Array<number | undefined | null>) => {
	const valid = values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
	if (!valid.length) return 0;
	return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const maxEntry = <T,>(
	values: T[],
	getter: (value: T) => number | undefined | null
): T | undefined => {
	let best: T | undefined;
	let bestValue = -Infinity;
	for (const value of values) {
		const candidate = getter(value);
		if (typeof candidate === 'number' && !Number.isNaN(candidate) && candidate > bestValue) {
			bestValue = candidate;
			best = value;
		}
	}
	return best;
};

const minEntry = <T,>(
	values: T[],
	getter: (value: T) => number | undefined | null
): T | undefined => {
	let best: T | undefined;
	let bestValue = Infinity;
	for (const value of values) {
		const candidate = getter(value);
		if (typeof candidate === 'number' && !Number.isNaN(candidate) && candidate < bestValue) {
			bestValue = candidate;
			best = value;
		}
	}
	return best;
};

const majorityDirection = (directions: Array<string | undefined | null>) => {
	const counts = new Map<string, number>();
	for (const dir of directions) {
		if (!dir) continue;
		const current = counts.get(dir) ?? 0;
		counts.set(dir, current + 1);
	}
	let bestDir = 'N/A';
	let bestCount = 0;
	for (const [dir, count] of counts) {
		if (count > bestCount) {
			bestDir = dir;
			bestCount = count;
		}
	}
	return bestDir;
};

export const DetailedGridPointData = ({ data }: DetailedGridPointDataProps) => {
	const spaces = useSpacePlanningStore((state) => state.spaces);

	if (data.length === 0) {
		return null;
	}

	// Get room display name if all selected cells are from the same unit
	const getRoomDisplayName = () => {
		const unitIds = Array.from(new Set(data.map(d => d.unitId).filter(Boolean)));
		if (unitIds.length !== 1) return null;

		const unitId = unitIds[0];

		// Find the room data matching this unit ID
		for (const spaceData of Object.values(spaces)) {
			const room = spaceData.rooms.find((r) => r.id === unitId);
			if (room) return room.spaceDescription;
		}

		return null;
	};

	const roomDisplayName = getRoomDisplayName();

	// Calculate room score for the selected region
	const roomScore = calculateSunlightScore(data);

	const aggregated = {
		count: data.length,
		averageDailySunlightHours: average(data.map((d) => d.averageDailySunlightHours)),
		consistencyScore: average(data.map((d) => d.consistencyScore)),
		winterMonthsAverage: average(data.map((d) => d.winterMonthsAverage)),
		springMonthsAverage: average(data.map((d) => d.springMonthsAverage)),
		summerMonthsAverage: average(data.map((d) => d.summerMonthsAverage)),
		fallMonthsAverage: average(data.map((d) => d.fallMonthsAverage)),
		peakSunlightHour: majorityDirection(data.map((d) => d.peakSunlightHour)),
		morningHoursPercent: average(data.map((d) => d.morningHoursPercent)),
		afternoonHoursPercent: average(data.map((d) => d.afternoonHoursPercent)),
		midDayHoursAverage: average(data.map((d) => d.midDayHoursAverage)),
		daylightAutonomyPercent: average(data.map((d) => d.daylightAutonomyPercent)),
		glareRiskHours: average(data.map((d) => d.glareRiskHours)),
		directionalBalance: average(data.map((d) => d.directionalBalance)),
		primarySunDirection: majorityDirection(data.map((d) => d.primarySunDirection)),
		longest: maxEntry(data, (d) => d.longestSunlightHours),
		shortest: minEntry(data, (d) => d.shortestSunlightHours),
		minViableDaysAverage: average(data.map((d) => d.minViableDaysCount)),
		totalDaysAverage: average(data.map((d) => d.totalDaysAnalyzed)),
		daysWithSunlightAverage: average(data.map((d) => d.daysWithSunlight)),
		zeroDaysAverage: average(data.map((d) => d.zeroDaysCount)),
		firstSunlightDate: minEntry(data, (d) => {
			const time = d.firstSunlightDate ? new Date(d.firstSunlightDate).getTime() : undefined;
			return time ?? undefined;
		})?.firstSunlightDate,
		lastSunlightDate: maxEntry(data, (d) => {
			const time = d.lastSunlightDate ? new Date(d.lastSunlightDate).getTime() : undefined;
			return time ?? undefined;
		})?.lastSunlightDate,
	};

	const consistencyColor =
		aggregated.consistencyScore > 0.7
			? '#00FF00'
			: aggregated.consistencyScore > 0.4
				? '#FFD700'
				: '#FF6B6B';
	const consistencyLevel =
		aggregated.consistencyScore > 0.7
			? '●●●'
			: aggregated.consistencyScore > 0.4
				? '●●○'
				: '●○○';

	const winterColor =
		aggregated.winterMonthsAverage > 4
			? '#90EE90'
			: aggregated.winterMonthsAverage > 2
				? '#FFD700'
				: '#FF6B6B';

	const autonomyColor =
		aggregated.daylightAutonomyPercent > 70
			? '#00FF00'
			: aggregated.daylightAutonomyPercent > 50
				? '#FFD700'
				: '#FF6B6B';

	const glareColor =
		aggregated.glareRiskHours < 1
			? '#00FF00'
			: aggregated.glareRiskHours < 2
				? '#FFD700'
				: '#FF6B6B';

	const balanceColor =
		aggregated.directionalBalance > 0.7
			? '#00FF00'
			: aggregated.directionalBalance > 0.4
				? '#FFD700'
				: '#FF6B6B';

	const viablePercent =
		aggregated.totalDaysAverage > 0
			? (aggregated.minViableDaysAverage / aggregated.totalDaysAverage) * 100
			: 0;
	const viableColor = viablePercent > 80 ? '#00FF00' : viablePercent > 50 ? '#FFD700' : '#FF6B6B';

	const getScoreColor = (score: number) => {
		if (score >= 7) return '#00FF00';
		if (score >= 5) return '#90EE90';
		if (score >= 3) return '#FFD700';
		return '#FF6B6B';
	};

	return (
		<div>
			<div className={styles.header}>
				<span className={styles.icon}>🌤️</span>
				<span className={styles.title}>
					{roomDisplayName ? `${roomDisplayName} (${aggregated.count} cells)` : `Aggregated Sunlight (${aggregated.count} cells)`}
				</span>
			</div>

			<div className={styles.section} style={{ borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '8px' }}>
				<div className={styles.sectionTitle}>Room Score</div>
				<div className={styles.row}>
					<span className={styles.label}>Overall:</span>
					<span className={styles.value} style={{ color: getScoreColor(roomScore.totalScore), fontSize: '1.2em', fontWeight: 'bold' }}>
						{roomScore.totalScore.toFixed(1)}/10
					</span>
					<span className={styles.date}>({roomScore.pointsAnalyzed} cells)</span>
				</div>
			</div>

			<div className={styles.primary}>
				<div className={styles.mainMetric}>
					{aggregated.averageDailySunlightHours.toFixed(1)} hrs/day
				</div>
				<div className={styles.consistency}>
					<span className={styles.label}>Consistency:</span>
					<span className={styles.value} style={{ color: consistencyColor }}>
						{(aggregated.consistencyScore * 100).toFixed(0)}% {consistencyLevel}
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Seasonal Analysis</div>
				<div className={styles.row}>
					<span className={styles.label}>Winter (Dec-Feb):</span>
					<span className={styles.value} style={{ color: winterColor }}>
						{aggregated.winterMonthsAverage.toFixed(1)}h
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Spring (Mar-May):</span>
					<span className={styles.value} style={{ color: '#98FB98' }}>
						{aggregated.springMonthsAverage.toFixed(1)}h
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Summer (Jun-Aug):</span>
					<span className={styles.value} style={{ color: '#FFA500' }}>
						{aggregated.summerMonthsAverage.toFixed(1)}h
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Fall (Sep-Nov):</span>
					<span className={styles.value} style={{ color: '#DEB887' }}>
						{aggregated.fallMonthsAverage.toFixed(1)}h
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Daily Pattern</div>
				<div className={styles.row}>
					<span className={styles.label}>Peak Hour:</span>
					<span className={styles.value} style={{ color: '#FFD700' }}>
						{aggregated.peakSunlightHour}
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>AM/PM Split:</span>
					<span className={styles.value} style={{ color: '#90EE90' }}>
						{aggregated.morningHoursPercent.toFixed(0)}% /{' '}
						{aggregated.afternoonHoursPercent.toFixed(0)}%
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Midday (10am-2pm):</span>
					<span className={styles.value} style={{ color: '#FFD700' }}>
						{aggregated.midDayHoursAverage.toFixed(1)}h
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Daylighting & Direction</div>
				<div className={styles.row}>
					<span className={styles.label}>Work Hours (8am-6pm):</span>
					<span className={styles.value} style={{ color: autonomyColor }}>
						{aggregated.daylightAutonomyPercent.toFixed(1)}%
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Glare Risk (&lt;15°):</span>
					<span className={styles.value} style={{ color: glareColor }}>
						{aggregated.glareRiskHours.toFixed(1)}h/day
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Primary Direction:</span>
					<span className={styles.value} style={{ color: '#87CEEB' }}>
						{aggregated.primarySunDirection}
					</span>
					<span className={styles.label} style={{ marginLeft: '10px' }}>
						Balance:
					</span>
					<span className={styles.value} style={{ color: balanceColor }}>
						{(aggregated.directionalBalance * 100).toFixed(0)}%
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Range</div>
				<div className={styles.row}>
					<span className={styles.label}>Best:</span>
					<span className={styles.value} style={{ color: '#90EE90' }}>
						{(aggregated.longest?.longestSunlightHours ?? 0).toFixed(1)}h
					</span>
					<span className={styles.date}>
						{formatDate(aggregated.longest?.longestSunlightDate)}
					</span>
					<span className={styles.label} style={{ marginLeft: '15px' }}>
						Worst:
					</span>
					<span className={styles.value} style={{ color: '#FF6B6B' }}>
						{(aggregated.shortest?.shortestSunlightHours ?? 0).toFixed(1)}h
					</span>
					<span className={styles.date}>
						{formatDate(aggregated.shortest?.shortestSunlightDate)}
					</span>
				</div>
			</div>

			<div className={styles.section}>
				<div className={styles.sectionTitle}>Viability</div>
				<div className={styles.row}>
					<span className={styles.label}>Good (&gt;4h):</span>
					<span className={styles.value} style={{ color: viableColor }}>
						{aggregated.minViableDaysAverage.toFixed(0)}/
						{aggregated.totalDaysAverage.toFixed(0)}
					</span>
					<span className={styles.date}>({viablePercent.toFixed(0)}%)</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Sunny Days:</span>
					<span className={styles.value} style={{ color: '#90EE90' }}>
						{aggregated.daysWithSunlightAverage.toFixed(0)}/
						{aggregated.totalDaysAverage.toFixed(0)}
					</span>
					<span className={styles.label} style={{ marginLeft: '15px' }}>
						No Sun:
					</span>
					<span
						className={styles.value}
						style={{
							color: aggregated.zeroDaysAverage === 0 ? '#00FF00' : '#FF6B6B',
						}}
					>
						{aggregated.zeroDaysAverage.toFixed(0)} days
					</span>
				</div>
			</div>

			<div className={styles.footer}>
				Analysis: {formatDate(aggregated.firstSunlightDate)} -{' '}
				{formatDate(aggregated.lastSunlightDate)}
			</div>
		</div>
	);
};
